"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.InvoiceRowContent = InvoiceRowContent;
/**
 * Zawartość wiersza listy faktur sprzedażowych.
 *
 * Układ z makiety `tmp/makieta-lista-faktur-v1.html`, wariant A („drzewko stron”), wybrany przez
 * właściciela z poprawką: bez nazwy kontraktu — wystarczy górna linia kontekstu (oznaczenie,
 * projekt, alias, typ umowy).
 *
 * Wiersz mówi tym samym językiem co rejestr pism i rejestr kontraktów:
 *  - kontekst na górze: plakietka oznaczenia kontraktu, projekt, alias, typ (jak w pismach);
 *  - podmioty trzecie schodzą pod nabywcę drzewkiem `├ └` — tym samym znakiem co sprawy w pismach;
 *  - daty stoją w jednej kolumnie bocznej (jak terminy w kontraktach), więc się nie zawijają;
 *  - stopka z właścicielem i aktualizacją siedzi POD treścią w lewej kolumnie, nie pod całym
 *    wierszem: pod kolumną kwot robiła pusty pas przy krótkich fakturach.
 *
 * Numer faktury nie zawija się nigdy: korekty mają numery w rodzaju `FV-K_1/175/2026` i łamały się
 * w kolumnie na dwie linie.
 *
 * Uwaga do stopki: `LastUpdated` stempluje baza przy każdym zapisie, ale `EditorId` wpisuje front
 * przy zapisie z modalu. Zapisy idące inną drogą (kopia faktury, aktualizacje statusów KSeF po
 * stronie serwera) potrafią zostawić poprzednie nazwisko — dlatego pole podpisane jest „aktualizacja”,
 * a nie „zmienił”. Puste pola (658 faktur bez edytora w bazie z 16.06) wychodzą jako „—”.
 */
const react_1 = __importDefault(require("react"));
const react_bootstrap_1 = require("react-bootstrap");
const Tools_1 = __importDefault(require("../../React/Tools/Tools"));
const ToolsDate_1 = __importDefault(require("../../React/Tools/ToolsDate"));
const CommonComponents_1 = require("../../View/Resultsets/CommonComponents");
const LetterRowContent_1 = require("../../Letters/LettersList/LetterRowContent");
const thirdPartyRoles_1 = require("./thirdPartyRoles");
/** Paleta wspólna z rejestrem pism i kontraktów; `tpInk` to kolor ról podmiotów trzecich. */
const C = {
    ink: "#16211c",
    inkSoft: "#3c4c44",
    muted: "#6b7c73",
    faint: "#94a49b",
    line: "#c2d1c9",
    accent: "#166243",
    warn: "#8f5406",
    rowLine: "#e6ece9",
    tpInk: "#4b5a90",
};
const S = {
    top: { display: "grid", gridTemplateColumns: "1fr 232px", gap: "18px", alignItems: "start" },
    stack: { display: "flex", flexDirection: "column", gap: "7px", minWidth: 0, wordBreak: "break-word" },
    ctx: { display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "7px", fontSize: ".8rem" },
    chip: {
        fontWeight: 700,
        borderRadius: "4px",
        padding: "1px 7px",
        fontSize: ".74rem",
        whiteSpace: "nowrap",
        fontVariantNumeric: "tabular-nums",
        color: "#fff",
        backgroundColor: C.accent,
    },
    proj: { color: C.muted, fontSize: ".76rem", fontVariantNumeric: "tabular-nums" },
    alias: { color: C.inkSoft, fontWeight: 600 },
    ctype: { color: C.faint },
    numLine: { display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "9px" },
    number: {
        fontSize: "1.06rem",
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
    },
    /** Numer korekty jest długi (`FV-K_1/175/2026`) — mniejszy stopień pisma zamiast zawijania. */
    numberCorrection: { fontSize: ".95rem" },
    buyer: { fontSize: ".98rem", fontWeight: 600, color: C.ink },
    buyerAddr: { color: C.muted, fontSize: ".76rem" },
    desc: { color: C.inkSoft, fontSize: ".88rem" },
    label: { color: C.faint },
    tpHead: {
        fontSize: ".66rem",
        textTransform: "uppercase",
        letterSpacing: ".08em",
        color: C.faint,
        fontWeight: 700,
        marginBottom: "3px",
    },
    tpRow: {
        fontSize: ".8rem",
        color: C.inkSoft,
        display: "grid",
        gridTemplateColumns: "15px 1fr",
    },
    tree: { color: C.line, fontFamily: 'ui-monospace, "Cascadia Mono", Consolas, monospace' },
    tpRole: {
        color: C.tpInk,
        fontSize: ".72rem",
        textTransform: "uppercase",
        letterSpacing: ".05em",
        fontWeight: 700,
    },
    tpName: { color: C.ink },
    tpNip: { color: C.faint, fontSize: ".72rem", fontVariantNumeric: "tabular-nums" },
    sideBox: { borderLeft: `1px solid ${C.rowLine}`, paddingLeft: "14px" },
    sideHead: {
        fontSize: ".66rem",
        textTransform: "uppercase",
        letterSpacing: ".08em",
        color: C.faint,
        fontWeight: 700,
        marginBottom: "3px",
    },
    sideRow: { display: "flex", justifyContent: "space-between", gap: "10px", fontSize: ".78rem", padding: "1px 0" },
    sideKey: {
        color: C.faint,
        textTransform: "uppercase",
        letterSpacing: ".06em",
        fontSize: ".66rem",
        fontWeight: 700,
    },
    sideValue: { color: C.inkSoft, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
    sideValueOverdue: {
        color: C.warn,
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
    },
    amount: {
        marginTop: "8px",
        paddingTop: "7px",
        borderTop: `1px solid ${C.rowLine}`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: "10px",
    },
    amountValue: { fontSize: "1.02rem", fontWeight: 700, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
    amountGross: { color: C.muted, fontSize: ".72rem", fontVariantNumeric: "tabular-nums", textAlign: "right" },
    foot: { fontSize: ".76rem", color: C.muted, marginTop: "8px" },
    footStrong: { color: C.inkSoft, fontWeight: 600 },
    dot: { color: C.line },
};
function personLabel(person) {
    if (!person)
        return "";
    return [person.name, person.surname].filter(Boolean).join(" ").trim();
}
function entityAddressLine(entity) {
    if (!entity)
        return "";
    return [entity.address, entity.taxNumber ? `NIP ${entity.taxNumber}` : ""].filter(Boolean).join(" · ");
}
/**
 * Podmioty trzecie: nowa lista `_thirdParties`, a gdy pusta — stare pojedyncze pole `_thirdParty`
 * (faktury sprzed migracji na tabelę InvoiceThirdParties nadal je mają).
 */
function thirdPartiesOf(invoice) {
    if (invoice._thirdParties?.length)
        return invoice._thirdParties.filter((item) => !!item._entity);
    if (invoice.includeThirdParty && invoice._thirdParty)
        return [{ role: null, _entity: invoice._thirdParty }];
    return [];
}
function InvoiceContext({ invoice }) {
    const contract = invoice._contract;
    if (!contract)
        return null;
    return (react_1.default.createElement("div", { style: S.ctx },
        contract.ourId && react_1.default.createElement("span", { style: S.chip }, contract.ourId),
        contract._project?.ourId && react_1.default.createElement("span", { style: S.proj }, contract._project.ourId),
        contract.alias && react_1.default.createElement("span", { style: S.alias }, contract.alias),
        contract._type?.name && react_1.default.createElement("span", { style: S.ctype }, contract._type.name)));
}
function ThirdParties({ invoice }) {
    const thirdParties = thirdPartiesOf(invoice);
    if (!thirdParties.length)
        return null;
    return (react_1.default.createElement("div", null,
        react_1.default.createElement("div", { style: S.tpHead }, "Podmioty trzecie"),
        thirdParties.map((thirdParty, index) => {
            const roleName = (0, thirdPartyRoles_1.thirdPartyRoleName)(thirdParty.role);
            return (react_1.default.createElement("div", { key: `${thirdParty._entity?.id}-${index}`, style: S.tpRow },
                react_1.default.createElement("span", { style: S.tree }, index === thirdParties.length - 1 ? "└" : "├"),
                react_1.default.createElement("span", null,
                    roleName && react_1.default.createElement("span", { style: S.tpRole },
                        roleName,
                        " "),
                    react_1.default.createElement("span", { style: S.tpName }, thirdParty._entity?.name),
                    " ",
                    thirdParty._entity?.taxNumber && react_1.default.createElement("span", { style: S.tpNip }, thirdParty._entity.taxNumber))));
        })));
}
/** Kolumna boczna: daty jedna pod drugą (koniec zawijania w wąskich kolumnach) i kwoty. */
function InvoiceDates({ invoice }) {
    const deadline = invoice.paymentDeadline;
    const deadlineOverdue = !!deadline && (0, LetterRowContent_1.isOverdue)(deadline);
    return (react_1.default.createElement("div", { style: S.sideBox },
        react_1.default.createElement("div", { style: S.sideHead }, "Terminy"),
        react_1.default.createElement("div", { style: S.sideRow },
            react_1.default.createElement("span", { style: S.sideKey }, "sprzeda\u017C"),
            react_1.default.createElement("span", { style: S.sideValue }, (0, LetterRowContent_1.formatDate)(invoice.issueDate))),
        react_1.default.createElement("div", { style: S.sideRow },
            react_1.default.createElement("span", { style: S.sideKey }, "wystawiono"),
            react_1.default.createElement("span", { style: S.sideValue }, (0, LetterRowContent_1.formatDate)(invoice.sentDate))),
        react_1.default.createElement("div", { style: S.sideRow },
            react_1.default.createElement("span", { style: S.sideKey }, "p\u0142atno\u015B\u0107 do"),
            react_1.default.createElement("span", { style: deadlineOverdue ? S.sideValueOverdue : S.sideValue }, (0, LetterRowContent_1.formatDate)(deadline))),
        invoice._totalNetValue !== undefined && invoice._totalNetValue !== null && (react_1.default.createElement(react_1.default.Fragment, null,
            react_1.default.createElement("div", { style: S.amount },
                react_1.default.createElement("span", { style: S.sideKey }, "netto, z\u0142"),
                react_1.default.createElement("span", { style: S.amountValue }, Tools_1.default.formatNumber(invoice._totalNetValue))),
            invoice._totalGrossValue !== undefined && invoice._totalGrossValue !== null && (react_1.default.createElement("div", { style: S.amountGross },
                "brutto: ",
                Tools_1.default.formatNumber(invoice._totalGrossValue)))))));
}
function InvoiceFooter({ invoice }) {
    const owner = personLabel(invoice._owner);
    const editor = personLabel(invoice._editor);
    const lastUpdated = invoice._lastUpdated ? ToolsDate_1.default.dateToDDmmmYYYYHHMM(invoice._lastUpdated) : "";
    if (!owner && !editor && !lastUpdated)
        return null;
    return (react_1.default.createElement("div", { style: S.foot },
        react_1.default.createElement("span", null,
            "w\u0142a\u015Bciciel: ",
            react_1.default.createElement("b", { style: S.footStrong }, owner || "—")),
        " ",
        react_1.default.createElement("span", { style: S.dot }, "\u00B7"),
        " ",
        react_1.default.createElement("span", { title: "Osoba i czas ostatniego zapisu faktury" },
            "aktualizacja: ",
            react_1.default.createElement("b", { style: S.footStrong }, editor || "—"),
            lastUpdated ? `, ${lastUpdated}` : "")));
}
function InvoiceRowContent({ invoice }) {
    const isCorrection = !!invoice.correctedInvoiceId;
    return (react_1.default.createElement("div", { className: "d-flex flex-column", style: { gap: "9px", wordBreak: "break-word" } },
        react_1.default.createElement("div", { style: S.top },
            react_1.default.createElement("div", { style: S.stack },
                react_1.default.createElement(InvoiceContext, { invoice: invoice }),
                react_1.default.createElement("div", { style: S.numLine },
                    invoice.number && (react_1.default.createElement("span", { style: isCorrection ? { ...S.number, ...S.numberCorrection } : S.number }, invoice.number)),
                    react_1.default.createElement(CommonComponents_1.InvoiceStatusBadge, { status: invoice.status }),
                    isCorrection && (react_1.default.createElement(react_bootstrap_1.Badge, { bg: "warning", text: "dark", style: { fontSize: "0.7em" } }, "Korekta")),
                    invoice._corrections && invoice._corrections.length > 0 && (react_1.default.createElement(react_bootstrap_1.Badge, { bg: "info", text: "light", style: { fontSize: "0.7em" } },
                        "Ma korekty (",
                        invoice._corrections.length,
                        ")"))),
                invoice._entity && (react_1.default.createElement("div", null,
                    react_1.default.createElement("div", { style: S.buyer }, invoice._entity.name),
                    entityAddressLine(invoice._entity) && (react_1.default.createElement("div", { style: S.buyerAddr }, entityAddressLine(invoice._entity))))),
                react_1.default.createElement(ThirdParties, { invoice: invoice }),
                invoice.description && (react_1.default.createElement("div", { style: S.desc },
                    react_1.default.createElement("span", { style: S.label }, "Opis:"),
                    " ",
                    invoice.description)),
                isCorrection && invoice.correctionReason && (react_1.default.createElement("div", { style: S.desc },
                    react_1.default.createElement("span", { style: S.label }, "Pow\u00F3d korekty:"),
                    " ",
                    invoice.correctionReason)),
                react_1.default.createElement(InvoiceFooter, { invoice: invoice })),
            react_1.default.createElement(InvoiceDates, { invoice: invoice }))));
}
